import { useEffect, useRef, useState } from 'react';

interface Props {
  /** PD date as YYYY-MM-DD (Europe/Berlin wall-clock). */
  date: string;
  /** PD end-time as HH:MM (Europe/Berlin wall-clock). */
  endTime: string;
}

interface BerlinNow {
  ymd: string;
  secondsOfDay: number;
}

/**
 * Resolves `now` to its Berlin wall-clock parts via Intl.DateTimeFormat —
 * mirrors the pattern in `utils/debug-mode.ts:14` so we never depend on the
 * device's local time zone (an iPad with wrong auto-TZ would otherwise show
 * a misleading countdown). Exported for unit tests.
 */
export function berlinNow(now: Date): BerlinNow {
  const fmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value])) as Record<
    string,
    string
  >;
  const ymd = `${parts.year}-${parts.month}-${parts.day}`;
  const hour = Number(parts.hour ?? '0');
  const minute = Number(parts.minute ?? '0');
  const second = Number(parts.second ?? '0');
  return { ymd, secondsOfDay: hour * 3600 + minute * 60 + second };
}

/**
 * Remaining seconds until `endTime` of the PD on `date`. Positive while
 * before the end, ≤ 0 once the PA is over. Exported for tests.
 *
 * Day comparison guards against the edge where the iPad clock crosses
 * midnight before the cron-driven 5-minute /kiosk/me re-poll lands.
 */
export function paRemainingSeconds(now: Date, date: string, endTime: string): number {
  const b = berlinNow(now);
  if (b.ymd > date) return -1;
  if (b.ymd < date) return Number.POSITIVE_INFINITY;
  const [h, m] = endTime.split(':').map(Number);
  const targetSecondsOfDay = (h ?? 0) * 3600 + (m ?? 0) * 60;
  return targetSecondsOfDay - b.secondsOfDay;
}

function formatRemaining(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

const ENDED_BANNER_VISIBLE_MS = 60_000;

/**
 * Header-companion that shows „PA endet in MM:SS" while a project day is
 * still running and a brief „PA beendet" notice for one minute after the
 * end, then unmounts. Returns `null` when there is nothing to show — the
 * parent can render it unconditionally as long as it has a valid date/
 * endTime pair.
 */
export function PaCountdown({ date, endTime }: Props): JSX.Element | null {
  const [now, setNow] = useState<Date>(() => new Date());
  // Track whether we were ever > 0 — required to decide if the post-end
  // banner should appear (only if the user watched the countdown reach 0).
  const sawPositiveRef = useRef<boolean>(paRemainingSeconds(new Date(), date, endTime) > 0);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(id);
  }, []);

  const remaining = paRemainingSeconds(now, date, endTime);

  if (remaining > 0) {
    sawPositiveRef.current = true;
    return (
      <span
        data-testid="pa-countdown"
        className="font-mono text-xs tabular-nums text-muted-foreground md:text-sm"
      >
        PA endet in {formatRemaining(remaining)}
      </span>
    );
  }

  // Post-end window: show "PA beendet" for ENDED_BANNER_VISIBLE_MS, then go
  // away. Only if we actually saw the countdown live — opening the page
  // long after the PA ended should NOT flash the banner.
  if (sawPositiveRef.current && remaining > -(ENDED_BANNER_VISIBLE_MS / 1_000)) {
    return (
      <span data-testid="pa-countdown-ended" className="text-xs text-muted-foreground md:text-sm">
        PA beendet
      </span>
    );
  }

  return null;
}
